<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40"
  >
    <div
      class="bg-white w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col max-h-[90vh]"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between bg-white px-6 py-4 rounded-t-xl flex-shrink-0"
      >
        <div class="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5 text-gray-800"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a3 3 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z"
            />
          </svg>
          <h2 class="text-lg font-semibold">Raise a Ticket</h2>
        </div>
        <button
          @click="$emit('close')"
          class="bg-red-500 rounded-md p-1 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-5 h-5 text-white"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Form -->
      <form
        @submit.prevent="submitTicket"
        class="flex flex-col gap-4 p-6 overflow-y-auto flex-grow"
      >
        <!-- Success / Error Flash -->
        <div
          v-if="flash.text"
          :class="
            flash.type === 'success'
              ? 'bg-green-50 border-green-400 text-green-700'
              : 'bg-red-50 border-red-400 text-red-700'
          "
          class="border rounded-md px-4 py-2 text-sm"
        >
          {{ flash.text }}
        </div>

        <!-- Row: Type + Priority -->
        <div class="flex gap-4 text-start">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Ticket Type <span class="text-red-500">*</span></label
            >
            <select
              v-model="form.ticket_type"
              required
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb]"
            >
              <option value="" disabled>Select type</option>
              <option value="bug">Bug / Issue Report</option>
              <option value="feature">Feature Request</option>
              <option value="support">General Support</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Priority <span class="text-red-500">*</span></label
            >
            <select
              v-model="form.priority"
              required
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb]"
            >
              <option value="" disabled>Select priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <!-- Title -->
        <div class="text-start">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Title <span class="text-red-500">*</span></label
          >
          <input
            v-model="form.title"
            type="text"
            required
            maxlength="255"
            placeholder="Brief summary of the issue..."
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb]"
          />
        </div>

        <!-- Description -->
        <div class="text-start">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Description <span class="text-red-500">*</span></label
          >
          <textarea
            v-model="form.description"
            required
            rows="3"
            placeholder="Describe the issue in detail..."
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb] resize-none"
          ></textarea>
        </div>

        <!-- Bug-specific fields -->
        <template v-if="form.ticket_type === 'bug'">
          <div class="text-start">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Steps to Reproduce</label
            >
            <textarea
              v-model="form.steps_to_reproduce"
              rows="3"
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb] resize-none"
            ></textarea>
          </div>
          <div class="flex gap-4 text-start">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Expected Behavior</label
              >
              <textarea
                v-model="form.expected_behavior"
                rows="2"
                placeholder="What should happen..."
                class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb] resize-none"
              ></textarea>
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Actual Behavior</label
              >
              <textarea
                v-model="form.actual_behavior"
                rows="2"
                placeholder="What actually happens..."
                class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb] resize-none"
              ></textarea>
            </div>
          </div>
        </template>

        <!-- Row: Module + Affected URL -->
        <div class="flex gap-4 text-start">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Module / Feature</label
            >
            <input
              v-model="form.module"
              type="text"
              maxlength="100"
              placeholder="e.g. Dashboard, Auth..."
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb]"
            />
          </div>
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Affected URL</label
            >
            <input
              v-model="form.affected_url"
              type="url"
              placeholder="https://..."
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#76bdeb]"
            />
          </div>
        </div>

        <!-- Attachments -->
        <div class="text-start">
          <label class="block text-sm font-medium text-gray-700 mb-1"
            >Attachments</label
          >
          <input
            type="file"
            multiple
            ref="fileInput"
            @change="onFileChange"
            class="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-white file:text-white hover:file:opacity-90 cursor-pointer border border-gray-300 rounded-lg"
          />
          <ul v-if="attachedFiles.length" class="mt-2 flex flex-wrap gap-2">
            <li
              v-for="(f, i) in attachedFiles"
              :key="i"
              class="flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-xs text-gray-600"
            >
              <span class="truncate max-w-[150px]">{{ f.name }}</span>
              <button
                type="button"
                @click="removeFile(i)"
                class="text-red-400 hover:text-red-600 font-bold leading-none"
              >
                &times;
              </button>
            </li>
          </ul>
        </div>
      </form>

      <!-- Footer -->
      <div
        class="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0"
      >
        <button
          type="button"
          @click="$emit('close')"
          class="px-5 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="submitTicket"
          :disabled="loading"
          class="px-5 py-2 text-sm rounded-lg bg-blues text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2"
        >
          <svg
            v-if="loading"
            class="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            ></path>
          </svg>
          {{ loading ? "Submitting..." : "Submit Ticket" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "RaiseTicket",
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "submitted"],
  data() {
    return {
      loading: false,
      flash: { text: "", type: "" },
      attachedFiles: [],
      form: {
        ticket_type: "",
        priority: "medium",
        title: "",
        description: "",
        steps_to_reproduce: "",
        expected_behavior: "",
        actual_behavior: "",
        module: "",
        affected_url: "",
      },
    };
  },
  computed: {
    ...mapGetters(["getAccessToken", "getUserInfo","userInfo"]),
  },
  watch: {
    isVisible(val) {
      if (val) this.resetForm();
    },
  },
  methods: {
    onFileChange(e) {
      this.attachedFiles = [
        ...this.attachedFiles,
        ...Array.from(e.target.files),
      ];
      this.$refs.fileInput.value = "";
    },
    removeFile(index) {
      this.attachedFiles.splice(index, 1);
    },
    resetForm() {
      this.form = {
        ticket_type: "",
        priority: "medium",
        title: "",
        description: "",
        steps_to_reproduce: "",
        expected_behavior: "",
        actual_behavior: "",
        module: "",
        affected_url: "",
      };
      this.attachedFiles = [];
      this.flash = { text: "", type: "" };
    },
    async submitTicket() {
      if (
        !this.form.ticket_type ||
        !this.form.priority ||
        !this.form.title ||
        !this.form.description
      ) {
        this.flash = {
          text: "Please fill in all required fields.",
          type: "error",
        };
        return;
      }

      this.loading = true;
      this.flash = { text: "", type: "" };

      try {
        const formData = new FormData();
        formData.append("organization", 18);
        formData.append("raised_by", this.userInfo?.user.email || "");
        formData.append("ticket_type", this.form.ticket_type);
        formData.append("project_name", "Alliance-LMS");
        formData.append("project_code", "AUO-LMS");
        formData.append("priority", this.form.priority);
        formData.append("title", this.form.title);
        formData.append("description", this.form.description);

        if (this.form.steps_to_reproduce)
          formData.append("steps_to_reproduce", this.form.steps_to_reproduce);
        if (this.form.expected_behavior)
          formData.append("expected_behavior", this.form.expected_behavior);
        if (this.form.actual_behavior)
          formData.append("actual_behavior", this.form.actual_behavior);
        if (this.form.module) formData.append("module", this.form.module);
        if (this.form.affected_url)
          formData.append("affected_url", this.form.affected_url);

        this.attachedFiles.forEach((file) => {
          formData.append("files", file);
        });

        const response = await axios.post(
          `${process.env.VUE_APP_TICKET_URL}/tickets/create-with-attachments/`,
          formData,
          {
            headers: {
              ecoauth:
                "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkb21haW4iOiJwbXMuY2hhZHVyYS5jb20iLCJ1c2VyX2lkIjo2LCJleHAiOjE4NzQwNTA5NDZ9._M_j0Xe6wxhT5ckdrQsf9BLhcCblw1BesfQEKb4X8R4",
              "Content-Type": "multipart/form-data",
            },
          },
        );

        if (response.data.success) {
          this.flash = {
            text: `Ticket ${response.data.data.ticket.ticket_number} raised successfully!`,
            type: "success",
          };
          this.$emit("submitted", response.data.data.ticket);
          setTimeout(() => this.$emit("close"), 3500);
        } else {
          this.flash = {
            text: response.data.message || "Failed to raise ticket.",
            type: "error",
          };
        }
      } catch (err) {
        const errorMsg = err.response?.data?.error
          ? JSON.stringify(err.response.data.error)
          : "Something went wrong. Please try again.";
        this.flash = { text: errorMsg, type: "error" };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
